import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

function ImageSearch() {
  const location = useLocation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [textQuery, setTextQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResponse, setSearchResponse] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');

  const formatRelativeDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    const days = Math.floor(seconds / 86400);
    if (days > 90) {
      return date.toLocaleDateString();
    }
    if (days > 0) {
      return `${days} days ago`;
    }

    const hours = Math.floor(seconds / 3600);
    if (hours > 0) {
      return `${hours} hours ago`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes} mins ago`;
    }
    return 'just now';
  };

  const handleSearch = useCallback(async (queryOverride) => {
    const currentQuery = queryOverride || textQuery;
    if (!selectedFile && !currentQuery.trim()) {
      setError('Please select an image or enter a search term.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSearchResponse(null);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';

    try {
      let response;
      if (currentQuery.trim()) {
        const searchUrl = `${backendUrl}/search-by-query`;
        const body = { query: currentQuery, top_k: 10 };
        if (dateFilter !== 'all') {
          body.days_filter = dateFilter;
        }
        response = await fetch(searchUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else if (selectedFile) {
        const formData = new FormData();
        formData.append('productImage', selectedFile);
        if (dateFilter !== 'all') {
          formData.append('days_filter', dateFilter);
        }
        const searchUrl = `${backendUrl}/search-by-image`;
        response = await fetch(searchUrl, {
          method: 'POST',
          body: formData,
        });
      } else {
        throw new Error('No search criteria provided.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown server error.' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSearchResponse(data);
    } catch (err) {
      console.error('Search failed:', err);
      setError(err.message || 'Failed to perform search.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, textQuery, dateFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryFromUrl = params.get('q');
    if (queryFromUrl) {
      setTextQuery(queryFromUrl);
      handleSearch(queryFromUrl);
    }
    // We only want this to run on initial load or if the URL query changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setTextQuery('');
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setSearchResponse(null); // Clear previous results
      setError(null);
    } else {
      setSelectedFile(null);
      setPreview(null);
    }
  };

  const handleTextChange = (event) => {
    setTextQuery(event.target.value);
    if (event.target.value) {
      setSelectedFile(null);
      setPreview(null);
      setSearchResponse(null);
      setError(null);
    }
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item size={{ xs:12 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCamera />}
              disabled={isLoading}
            >
              Upload Product Image
              <input
                type="file"
                hidden
                accept="image/*"
                capture="camera"
                onChange={handleFileChange}
              />
            </Button>

            {preview && (
              <Card sx={{ maxWidth: 300, mt: 1 }}>
                <CardMedia
                  component="img"
                  height="180"
                  image={preview}
                  alt="Product preview"
                  sx={{ objectFit: 'contain', border: '1px solid #ddd' }}
                />
              </Card>
            )}

            <Divider sx={{ my: 1 }}>OR</Divider>

            <TextField
              label="Search by item name"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              size='small'
              value={textQuery}
              onChange={handleTextChange}
              disabled={isLoading}
            />

            <Divider sx={{ mb: 2 }} />

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="date-filter-label">Date Range</InputLabel>
              <Select
                labelId="date-filter-label"
                id="date-filter-select"
                value={dateFilter}
                label="Date Range"
                onChange={handleDateFilterChange}
                disabled={isLoading}
              >
                <MenuItem value={7}>Last 7 Days</MenuItem>
                <MenuItem value={14}>Last 14 Days</MenuItem>
                <MenuItem value={30}>Last 30 Days</MenuItem>
                <MenuItem value={90}>Last 90 Days</MenuItem>
                <MenuItem value="all">All Time</MenuItem>                
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={(!selectedFile && !textQuery.trim()) || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            >
              {isLoading ? 'Searching...' : 'Search Past Purchases'}
            </Button>
          </Box>
        </Grid>

        <Grid item size={{ xs: 12, md: 8 }}>          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {searchResponse && (
            <Box mt={2}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="body1" color="text.secondary">
                    {searchResponse.query ? 
                      <>Searched for: <br/><strong>{searchResponse.query}</strong></> :
                      searchResponse.image_description ?
                      <>Detected from image:<br/><strong>{searchResponse.image_description}</strong></> :
                      'Search results:'
                    }
                  </Typography>                  
                </CardContent>
              </Card>

              <Typography variant="subtitle1" gutterBottom>
                Possible matches:
              </Typography>
              {searchResponse.results && searchResponse.results.length > 0 ? (
                <List>
                  {searchResponse.results.map((item, index) => (
                    <React.Fragment key={item.pinecone_id || item.item_details.id}>
                      <ListItem alignItems="flex-start" sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                        <ListItemText
                          primary={<strong>{item.item_details.proper_name || item.item_details.short_name}</strong>}
                          secondary={
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 0.5 }}>
                                <Typography variant="body2" color="text.primary" sx={{ minWidth: '45px' }}>
                                  Match:
                                </Typography>
                                <Box sx={{ width: '100%', mx: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={item.pinecone_score ? item.pinecone_score * 100 : 0}
                                    color={item.pinecone_score > 0.75 ? "warning" : "primary"}
                                  />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                  <Typography variant="body2" color="text.secondary">{`${item.pinecone_score ? Math.round(item.pinecone_score * 100) : 0}%`}</Typography>
                                </Box>
                              </Box>
                              {item.item_details.receipt_store_name && <Typography component="span" display="block">Store: {item.item_details.receipt_store_name}</Typography>}
                              {item.item_details.receipt_transaction_date && <Typography component="span" display="block">Date: {formatRelativeDate(item.item_details.receipt_transaction_date)}</Typography>}
                              <Typography component="span" display="block">
                                Price: £{item.item_details.total_price !== null ? Number(item.item_details.total_price).toFixed(2) : 'N/A'}
                                {item.item_details.is_discounted && <small> (discounted) </small>}
                              </Typography>
                              {item.item_details.quantity !== null && <Typography component="span" display="block">Quantity: {Number(item.item_details.quantity)}</Typography>}
                              {item.item_details.receipt_image_url && (
                                <Link href={item.item_details.receipt_image_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', mt: 1, fontSize: '0.875rem' }}>
                                  View Receipt
                                  <OpenInNewIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                                </Link>
                              )}
                              {item.error && <Alert severity="warning" sx={{mt: 1, fontSize: '0.8rem'}}>{item.error}</Alert>}
                            </>
                          }
                        />
                      </ListItem>
                      {index < searchResponse.results.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography>No similar items found in your purchase history.</Typography>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ImageSearch;